import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import type { ItineraryDay, TripPhoto } from '../../types';
import { useDateLocale } from '../../utils/dateLocale';
import { tripPhotoUrl } from '../../utils/tripPhotos';
import Modal from '../ui/Modal';
import { ChevronLeft, ChevronRight, Download, Icon, MapPin, Sparkles, Trash2 } from '../ui/Icon';

interface Props {
  /** The photos the arrows cycle through. */
  photos: TripPhoto[];
  index: number;
  days: ItineraryDay[];
  canEdit: boolean;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  onMoveToDay: (photo: TripPhoto, dayId: string | null) => void;
  /** ADR-0042: the stop is chosen among the photo's own day's stops. */
  onChangeStop: (photo: TripPhoto, activityId: string | null) => void;
  /** ADR-0058: ask the AI where this photo was taken. Only the Diary offers it. */
  onSuggestStop?: (photo: TripPhoto) => void;
  onDelete: (photo: TripPhoto) => void;
}

export default function PhotoLightbox({ photos, index, days, canEdit, onIndexChange, onClose, onMoveToDay, onChangeStop, onSuggestStop, onDelete }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const photo = photos[index];
  const step = (delta: number) => onIndexChange((index + delta + photos.length) % photos.length);

  useEffect(() => {
    if (photos.length < 2) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      // On the day/stop selects the arrows change the option, not the photo.
      if (e.target instanceof HTMLElement && e.target.closest('input, select, textarea')) return;
      e.preventDefault();
      step(e.key === 'ArrowRight' ? 1 : -1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!photo) return null;
  const stops = days.find(d => d.id === photo.dayId)?.activities.filter(a => !a.isOrigin) ?? [];
  // takenAt is the camera's wall clock: only its date part is meaningful.
  const shownDate = photo.takenAt ? parseISO(photo.takenAt.slice(0, 10)) : parseISO(photo.createdAt);

  return (
    <Modal open onClose={onClose} title="" size="lg" noPadding>
      <div className="relative bg-black flex flex-col">
        <img src={tripPhotoUrl(photo.filename)} alt={photo.originalName} className="w-full max-h-[75vh] object-contain" />
        {photos.length > 1 && (
          <>
            <button
              onClick={() => step(-1)}
              aria-label={t('journal.previousPhoto')}
              className="absolute left-2 top-1/2 -translate-y-1/2 btn-icon w-8 h-8 bg-black/40 hover:bg-black/60 text-white border-0"
            >
              <Icon as={ChevronLeft} size={16} />
            </button>
            <button
              onClick={() => step(1)}
              aria-label={t('journal.nextPhoto')}
              className="absolute right-2 top-1/2 -translate-y-1/2 btn-icon w-8 h-8 bg-black/40 hover:bg-black/60 text-white border-0"
            >
              <Icon as={ChevronRight} size={16} />
            </button>
          </>
        )}
        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-white dark:bg-slate-900 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">
              {t('gallery.uploadedBy', { name: photo.uploadedBy.displayName || photo.uploadedBy.username })}
            </p>
            <p className="text-[11px] text-slate-400">{format(shownDate, 'PPP', { locale: dateLocale })}</p>
            {photo.activity && !canEdit && (
              <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400 truncate">
                <Icon as={MapPin} size={11} className="shrink-0" />
                <span className="truncate">{photo.activity.title}</span>
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {canEdit && (
              <select
                value={photo.dayId ?? ''}
                onChange={e => onMoveToDay(photo, e.target.value || null)}
                className="text-xs border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 cursor-pointer"
              >
                <option value="">{t('gallery.unassignedDay')}</option>
                {days.map(d => (
                  <option key={d.id} value={d.id}>{t('gallery.dayOption', { number: d.dayNumber })}</option>
                ))}
              </select>
            )}
            {canEdit && stops.length > 0 && (
              <select
                value={photo.activityId ?? ''}
                onChange={e => onChangeStop(photo, e.target.value || null)}
                aria-label={t('gallery.stopLabel')}
                className="max-w-[11rem] truncate text-xs border border-slate-200 dark:border-slate-700 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 cursor-pointer"
              >
                <option value="">{t('gallery.noStop')}</option>
                {stops.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
              </select>
            )}
            {canEdit && onSuggestStop && days.some(d => d.activities.some(a => !a.isOrigin)) && (
              <button
                onClick={() => onSuggestStop(photo)}
                className="btn-icon w-8 h-8 text-teal-600"
                title={t('gallery.suggest.hint')}
                aria-label={t('gallery.suggest.button')}
              >
                <Icon as={Sparkles} size={15} />
              </button>
            )}
            <a
              href={tripPhotoUrl(photo.filename)}
              download={photo.originalName}
              className="btn-icon w-8 h-8 text-slate-500"
              title={t('gallery.download')}
            >
              <Icon as={Download} size={15} />
            </a>
            {canEdit && (
              <button onClick={() => onDelete(photo)} className="btn-icon w-8 h-8 text-red-500" title={t('gallery.delete')}>
                <Icon as={Trash2} size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
